<script setup>
import { ref, onMounted, watch } from "vue";
import { getTotemTicketById, getTotemTickets } from "@/services/api/TotemTickets.js";
import { TicketState } from "@/enums/enums.js";
import { initEcho } from "@/services/api/echo.js";
import {refreshToken} from "@/services/api/auth.js";
import ToggleFullscreenBtn from "@/components/Layouts/TopHeader/ToggleFullscreenBtn.vue";

const currentCall = ref(null);
const lastCalls = ref(null);
const time = ref("");

let callInterval = null;
const audio = new Audio("/audios/call.mp3");
let audioUnlocked = false;
const isFullscreen = ref(false)


const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen()
        isFullscreen.value = true
    } else {
        await document.exitFullscreen()
        isFullscreen.value = false
    }
}
function unlockAudio() {

    if (audioUnlocked) return;

    audio.play()
        .then(() => {
            audio.pause();
            audio.currentTime = 0;
            audioUnlocked = true;
        })
        .catch(() => {});

}
function updateClock() {
    const now = new Date();
    time.value = now.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
    });
}

function listenTicketCalls() {

    const echo = initEcho();

    echo.channel("totem-calls")
        .listen("TicketCalled", (event) => {
            currentCall.value = event.ticket;
        });
}

function playSoundAndSpeak() {

    if (!currentCall.value) return;
    audio.currentTime = 0;

    audio.play().then(() => {
        const text = `${currentCall.value.customer_name}, dirigir-se ao guichê ${currentCall.value.counter_name}`;

        const speech = new SpeechSynthesisUtterance(text);
        speech.lang = "pt-BR";
        speech.rate = 0.9;
        speech.volume = 1;

        speechSynthesis.speak(speech);



    }).catch(() => {
        console.warn("Não foi possível tocar o áudio automaticamente.");
    });
}

watch(currentCall, (newCall) => {

    if (!newCall) return;

    if (callInterval) {
        clearInterval(callInterval);
    }

    callInterval = setInterval(async () => {
        await loadCurrentCall();
        playSoundAndSpeak();
        await refreshToken();
    }, 18000);

});

async function loadCurrentCall() {
    const responseFinishedTotem = await getTotemTickets({ limit: 7, state: TicketState.Finished, orderBy:"updated_at desc" });
    const currentCallResponse = await getTotemTickets({ limit: 1, state: TicketState.Calling });

    if (currentCall.value?.id !== currentCallResponse.data[0]?.id) {
        currentCall.value = currentCallResponse.data[0];
    }
    const tickets = responseFinishedTotem.data;

    const details = await Promise.all(
        tickets.map(t => getTotemTicketById({ id: t.id }))
    );

    lastCalls.value = details.map(r => r.data);
}

onMounted(async () => {

    await loadCurrentCall();

    listenTicketCalls();

    updateClock();

    document.addEventListener("click", unlockAudio, { once: true });
    document.addEventListener("keydown", unlockAudio, { once: true });

    setInterval(updateClock, 1000);
});
</script>

<template>
    <header class="pt-6 px-10 bg-white h-70">
        <v-row cols="12">
            <div class="d-flex align-center ga-3">

                <v-avatar color="teal" size="40">
                    <v-icon color="white">mdi-ticket-confirmation-outline</v-icon>
                </v-avatar>

                <div class="d-flex flex-column">
                <span class="text-h6 font-weight-bold">
                  Check-In Loja Calçadão
                </span>
                <span class="text-caption text-grey">
                  Acompanhe sua chamada de atendimento
                </span>
            </div>

            </div>

            <v-spacer />

            <div class="d-flex align-center ga-2 text-teal-darken-2 font-weight-medium">
                <v-icon size="20">mdi-monitor</v-icon>
                FUNIL X
            </div>
            <v-btn
                icon
                variant="text"
                @click="toggleFullscreen"
            >
                <i class="mdi" :class="isFullscreen ? 'd-none' : 'mdi-fullscreen'"></i>
            </v-btn>

        </v-row>
    </header>
    <div class="panel-wrapper">

        <v-row class="pa-6 ma-0 fill-height">

            <!-- CHAMADA ATUAL -->
            <v-col cols="12" lg="8">
                <v-card
                    class="pa-10 d-flex flex-column align-center justify-center text-center main-card"
                    color="teal-darken-2"
                    rounded="xl"
                >
                    <div class="text-h6 text-grey-lighten-2 mb-4 font-weight-bold">
                        CHAMANDO
                    </div>

                    <div class="text-h1 font-weight-bold text-white mb-8">
                        {{ currentCall?.customer_name }}
                    </div>

                    <v-chip
                        size="x-large"
                        class="guiche-chip text-h5 text-white "
                    >
                        <v-icon color="white" start>mdi-door</v-icon>
                        <span class="text-h3 text-white font-weight-bold "> Guichê {{ currentCall?.counter_name }} </span>
                    </v-chip>
                </v-card>
            </v-col>

            <!-- ÚLTIMAS CHAMADAS -->
            <v-col cols="12" lg="4">
                <v-card class="pa-4 side-card d-flex flex-column h-100" rounded="xl">

                    <!-- TÍTULO -->
                    <div class="d-flex align-center mb-4 title-calls">
                        <v-icon size="18" class="mr-2">mdi-history</v-icon>
                        <span>Últimas Chamadas</span>
                    </div>

                    <!-- LISTA -->
                    <div class="calls-list flex-grow-18">

                        <div
                            v-for="(call, i) in lastCalls"
                            :key="i"
                            class="call-item mb-3"
                            :class="{ active: i === 0 }"
                        >
                            <div>
                                <div class="label">NOME</div>
                                <div class="name">{{ call?.customer_name }}</div>
                            </div>

                            <div class="call-right">
                                <div class="label">LOCAL</div>
                                <div class="counter">Guichê {{ call?.checkin_counter?.name }}</div>
                            </div>
                        </div>

                    </div>

                    <!-- ALERTA -->
                    <v-alert
                        type="info"
                        variant="tonal"
                        density="comfortable"
                        rounded="lg"
                        class="mt-auto"
                    >
                        Acesse o totem para gerar um ticket de atendimento.
                    </v-alert>

                </v-card>
            </v-col>
        </v-row>

        <!-- RODAPÉ -->
        <v-footer
            class="footer-panel d-flex justify-space-between px-6"
            color="blue-grey-darken-4"
        >
            <div class="text-white">
                <v-icon size="18" class="mr-1 mb-1">mdi-clock</v-icon>
                 {{ time }}
            </div>

            <div class="text-grey-lighten-2">
                Aguarde sua vez. Em breve você será chamado.
            </div>

            <div class="text-grey-lighten-1">
                CHECKIN
            </div>
        </v-footer>

    </div>
</template>

<style scoped>

.panel-wrapper{
    min-height:100vh;
    background:#f5f6f7;
}

.fill-height{
    min-height:calc(100vh - 50px);
}

.main-card{
    height:100%;
}

.footer-panel{
    position:fixed;
    bottom:0;
    width:100%;
    height:50px;
}

/* título */
.title-calls{
    font-weight:600;
    color:#5c6f7a;
}

.side-card{
    background:#f7f8f9;
    height:100%;
    display:flex;
    flex-direction:column;
}

/* lista ocupa o espaço disponível */
.calls-list{
    flex:1;
}

/* restante do seu css continua igual */
.call-item{
    display:flex;
    justify-content:space-between;
    align-items:center;
    padding:14px 16px;
    border-radius:12px;
    background:white;
    border-left:5px solid #e1e5e8;
    box-shadow:0 1px 2px rgba(0,0,0,0.05);
    opacity: 0.8;
}

.call-item.active{
    border-left:5px solid #2f7e8a;
    opacity: 1;
}

.label{
    font-size:11px;
    letter-spacing:1px;
    color:#9aa5ad;
    margin-bottom:2px;
}

.name{
    font-weight:700;
    font-size:18px;
    color:#2d3438;
}

.call-right{
    text-align:right;
}

.counter{
    font-weight:600;
    color:#6c7a80;
}

.guiche-chip {
    background: linear-gradient(180deg, #4f8b9a 0%, #00576d 100%)!important;
    border-radius: 20px;
    border: 1px solid rgba(255,255,255,0.25);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.15);
    font-weight: 600;
    letter-spacing: 0.5px;
    padding: 37px!important;
}

</style>
